<script lang="ts">
import GuestLayout from "@/layouts/GuestLayout.vue"

export default { layout: GuestLayout }
</script>

<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3"
import { shallowRef, useTemplateRef } from "vue"
import { z } from "zod"

import FlashAlerts from "@/components/FlashAlerts.vue"
import { newSessionPath } from "@/routes"

const props = defineProps<{
  submit_url: string
}>()

const loading = shallowRef(false)
const authFormRef = useTemplateRef("authFormRef")

const schema = z
  .object({
    first_name: z.string().min(1, "First name is required"),
    last_name: z.string().min(1, "Last name is required"),
    email: z.string().email("Please enter a valid email address"),
    password: z.string().min(8, "Password must be at least 8 characters"),
    password_confirmation: z.string().min(1, "Please confirm your password"),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: "Passwords must match",
    path: ["password_confirmation"],
  })

function submit() {
  if (!authFormRef.value) return
  loading.value = true
  router.post(
    props.submit_url,
    { registration: authFormRef.value.state },
    {
      onFinish: () => {
        loading.value = false
      },
    },
  )
}
</script>

<template>
  <Head title="Create Account" />
  <UAuthForm
    ref="authFormRef"
    title="Create your account"
    description="Enter your details to accept the invitation."
    :schema="schema"
    :fields="[
      {
        name: 'first_name',
        label: 'First name',
        type: 'text',
        required: true,
        autocomplete: 'given-name',
      },
      {
        name: 'last_name',
        label: 'Last name',
        type: 'text',
        required: true,
        autocomplete: 'family-name',
      },
      {
        name: 'email',
        label: 'Email',
        type: 'email',
        required: true,
        placeholder: 'you@example.com',
        autocomplete: 'username',
      },
      {
        name: 'password',
        label: 'Password',
        type: 'password',
        required: true,
        autocomplete: 'new-password',
      },
      {
        name: 'password_confirmation',
        label: 'Confirm password',
        type: 'password',
        required: true,
        autocomplete: 'new-password',
      },
    ]"
    :loading="loading"
    :submit="{ label: 'Create Account' }"
    @submit="submit"
  >
    <template #validation>
      <FlashAlerts />
    </template>

    <template #footer>
      <UButton :to="newSessionPath()" variant="link" label="Back to sign in" class="text-muted" />
    </template>
  </UAuthForm>
</template>
